import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../helpers/AuthContext';
import Alert from '../../Alert';
import useDebouncedAutosave from './useDebouncedAutosave';

const CONTENT_WARNINGS = [
    { value: 'bdsm', label: 'BDSM — Bondage and/or Sadomasochism' },
    { value: 'cnc', label: 'CNC — Consensual Non-Consent' },
    { value: 'rough', label: 'Rough Sex — Including choking, hair pulling, slapping' },
    { value: 'breathplay', label: 'Breath Play' },
    { value: 'public', label: 'Filmed in Public (Legally)' },
    { value: 'firearms', label: 'Firearm / Prop Firearms' },
];

export default function ProductWizardCompliance({
    product,
    onNext,
    onBack,
    onSaveAndExit,
    onProductUpdated,
}) {
    const { fetch, API_URL } = useAuth();

    const [values, setValues] = useState({
        dateFilmed: product?.dateFilmed || '',
        locationFilmed: product?.locationFilmed || '',
        custodianOfRecords: product?.custodianOfRecords || '',
        contentWarnings: product?.contentWarnings || [],
    });

    const [performers, setPerformers] = useState({
        productPerformers: [],
        channelPerformers: [],
        complianceInvitations: product?.metadata?.complianceInvitations || [],
    });
    const [performersLoading, setPerformersLoading] = useState(true);

    const [requestedAt, setRequestedAt] = useState(product?.requestedRedReviewAt || null);
    const [isFlagged, setIsFlagged] = useState(product?.isFlagged ?? false);

    const [addPerformerUuid, setAddPerformerUuid] = useState('');
    const [inviteEmail, setInviteEmail] = useState('');

    const [complianceError, setComplianceError] = useState('');
    const [complianceSaving, setComplianceSaving] = useState(false);
    const [performerMsg, setPerformerMsg] = useState('');
    const [performerError, setPerformerError] = useState(null);
    const [reviewMsg, setReviewMsg] = useState('');
    const [reviewError, setReviewError] = useState('');
    const saveQueue = useRef(Promise.resolve());

    useEffect(() => {
        loadPerformers();
    }, [product?.uuid]);

    const loadPerformers = async () => {
        setPerformersLoading(true);
        setPerformerError('');
        try {
            const res = await fetch(`${API_URL}/v1/seller/products/${product.uuid}/performers`);
            const json = await res.json();
            if (!res.ok) {
                throw new Error(json.error || json.message || 'Failed to load performers');
            }
            if (json.data) setPerformers(json.data);
        } catch (e) {
            setPerformerError(e.message || 'Failed to load performers');
        } finally {
            setPerformersLoading(false);
        }
    };

    const requestCompliancePatch = async payload => {
        const res = await fetch(`${API_URL}/v1/seller/products/${product.uuid}/compliance`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || json.message || 'Failed to save compliance');
        if (json.data) onProductUpdated?.(json.data);
        if (json.meta?.flagged) setIsFlagged(true);
        return json;
    };
    const patchCompliance = (payload = values) => {
        const save = saveQueue.current
            .catch(() => undefined)
            .then(() => requestCompliancePatch(payload));
        saveQueue.current = save;
        return save;
    };
    const {cancelAutosave} = useDebouncedAutosave(
        values,
        async payload => {
            try {
                await patchCompliance(payload);
                setComplianceError('');
            } catch (saveError) {
                setComplianceError(saveError.message);
                throw saveError;
            }
        },
        {enabled: Boolean(product?.uuid)},
    );

    const handleNext = async () => {
        cancelAutosave();
        setComplianceError('');
        setComplianceSaving(true);
        try {
            await patchCompliance();
            onNext(null);
        } catch (e) {
            setComplianceError(e.message);
        } finally {
            setComplianceSaving(false);
        }
    };

    const handleSaveAndExit = async () => {
        cancelAutosave();
        setComplianceError('');
        setComplianceSaving(true);
        try {
            await patchCompliance();
            onSaveAndExit(null);
        } catch (e) {
            setComplianceError(e.message);
        } finally {
            setComplianceSaving(false);
        }
    };

    const toggleWarning = (value) => {
        setValues(prev => ({
            ...prev,
            contentWarnings: prev.contentWarnings.includes(value)
                ? prev.contentWarnings.filter(w => w !== value)
                : [...prev.contentWarnings, value],
        }));
    };

    const handleAddPerformer = async (e) => {
        e.preventDefault();
        setPerformerMsg('');
        setPerformerError('');
        if (!addPerformerUuid && !inviteEmail) return;

        try {
            const body = addPerformerUuid
                ? { performerUuid: addPerformerUuid }
                : { performerEmail: inviteEmail };

            const res = await fetch(`${API_URL}/v1/seller/products/${product.uuid}/performers`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || json.message || 'Failed to add performer');

            setPerformerMsg(json.meta?.message || 'Performer added successfully.');
            setAddPerformerUuid('');
            setInviteEmail('');
            await loadPerformers();
        } catch (e) {
            setPerformerError(e.message);
        }
    };

    const handleRequestReview = async () => {
        setReviewMsg('');
        setReviewError('');
        try {
            const res = await fetch(`${API_URL}/v1/seller/products/${product.uuid}/compliance/request-review`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || json.message || 'Failed to submit review request');
            setRequestedAt(json.data?.requestedRedReviewAt || new Date().toISOString());
            setReviewMsg('Review request submitted. You will be notified via email when the review is complete.');
        } catch (e) {
            setReviewError(e.message);
        }
    };

    const isSaving = complianceSaving;

    return (
        <div className="wizard-step">

            {isFlagged && (
                <div className="flash warning" style={{marginBottom: '1.5rem'}}>
                    <h4 style={{marginTop: 0}}>Product Needs Review</h4>
                    {!requestedAt ? (
                        <p>Adult products need to be reviewed for legal compliance before they can be published. Please complete the compliance form and performers section below, then submit for review.</p>
                    ) : (
                        <p>You requested a review at {new Date(requestedAt).toLocaleString()}. We will email you with the results.</p>
                    )}
                    <ol>
                        <li>Make sure the listing is ready to publish.</li>
                        <li>Complete the <strong>2257 Compliance: Product</strong> form below.</li>
                        <li>Complete the <strong>2257 Compliance: Performers</strong> section below.</li>
                        <li>Click <strong>Request Review</strong> and wait for approval.</li>
                    </ol>
                    {reviewError && <Alert type="error">{reviewError}</Alert>}
                    {reviewMsg && <Alert type="success">{reviewMsg}</Alert>}
                    {!requestedAt && !reviewMsg && (
                        <button type="button" className="button" onClick={handleRequestReview}>
                            Request Review
                        </button>
                    )}
                </div>
            )}

            <h3>2257 Compliance: Product</h3>
            <p>2257 compliance is required for all products that feature adult content.</p>

            <div className="group">
                <label htmlFor="wc-dateFilmed">Date Filmed / Produced</label>
                <input
                    id="wc-dateFilmed"
                    type="date"
                    value={values.dateFilmed}
                    onChange={e => setValues(v => ({ ...v, dateFilmed: e.target.value }))}
                />
                <p className="help">Used to verify the age of performers at the time of filming.</p>
            </div>

            <div className="group">
                <label htmlFor="wc-locationFilmed">Location of Filming (City, State, Country)</label>
                <input
                    id="wc-locationFilmed"
                    type="text"
                    value={values.locationFilmed}
                    onChange={e => setValues(v => ({ ...v, locationFilmed: e.target.value }))}
                    placeholder="e.g. Los Angeles, CA, USA"
                />
                <p className="help">Ensures the product was produced in compliance with local laws.</p>
            </div>

            <div className="group">
                <label htmlFor="wc-custodian">Custodian of Records</label>
                <input
                    id="wc-custodian"
                    type="text"
                    value={values.custodianOfRecords}
                    onChange={e => setValues(v => ({ ...v, custodianOfRecords: e.target.value }))}
                    placeholder="e.g. Whatever Productions, Inc. 1234 Main St, City, State, Zip"
                />
                <p className="help">By law, the producer must retain proof of age and identity of all performers for 7 years, and publish the custodian of records address.</p>
            </div>

            <div className="group">
                <label>Content Warnings — Select all that apply</label>
                {CONTENT_WARNINGS.map(w => (
                    <label key={w.value} className="checkbox">
                        <input
                            type="checkbox"
                            checked={values.contentWarnings.includes(w.value)}
                            onChange={() => toggleWarning(w.value)}
                        />
                        <span>{w.label}</span>
                    </label>
                ))}
                <p className="help">These types of content may require additional safeguards before approval.</p>
            </div>

            {complianceError && <Alert type="error">{complianceError}</Alert>}

            <hr style={{margin: '1.5rem 0'}} />

            <h3>2257 Compliance: Performers</h3>
            <p>All performers must be over 18 and have provided valid ID and agreed to our Model Release and Consent form.</p>

            {performersLoading ? (
                <p>Loading performers…</p>
            ) : (
                <table style={{width: '100%', marginBottom: '1rem'}}>
                    <thead>
                        <tr>
                            <th>Performer</th>
                            <th>Compliance Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {performers.complianceInvitations.map(invite => (
                            <tr key={invite.email}>
                                <td>{invite.email}</td>
                                <td>Invited</td>
                            </tr>
                        ))}
                        {performers.productPerformers.map(p => (
                            <tr key={p.uuid}>
                                <td>{p.name}</td>
                                <td>{p.AgeVerification?.status
                                    ? p.AgeVerification.status.charAt(0).toUpperCase() + p.AgeVerification.status.slice(1)
                                    : 'Pending'
                                }</td>
                            </tr>
                        ))}
                        {performers.productPerformers.length === 0 && performers.complianceInvitations.length === 0 && (
                            <tr>
                                <td colSpan={2} className="text-subtle" style={{fontStyle: 'italic'}}>No performers added yet.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            )}

            <form onSubmit={handleAddPerformer}>
                {performers.channelPerformers.length > 0 && (
                    <div className="group">
                        <label htmlFor="wc-addPerformer">Add Previous Performer</label>
                        <select
                            id="wc-addPerformer"
                            value={addPerformerUuid}
                            onChange={e => { setAddPerformerUuid(e.target.value); setInviteEmail(''); }}
                        >
                            <option value="">— Select a performer —</option>
                            {performers.channelPerformers.map(p => (
                                <option key={p.uuid} value={p.uuid}>{p.name}</option>
                            ))}
                        </select>
                        <p className="help">If you don't appear in the list, <a href="/account/compliance" target="_blank">complete your model release</a> first.</p>
                    </div>
                )}
                <div className="group">
                    <label htmlFor="wc-inviteEmail">Invite New Performer by Email</label>
                    <input
                        id="wc-inviteEmail"
                        type="email"
                        value={inviteEmail}
                        onChange={e => { setInviteEmail(e.target.value); setAddPerformerUuid(''); }}
                        placeholder="performer@example.com"
                    />
                    <p className="help">They'll receive an email with instructions for providing 2257 compliance information.</p>
                </div>
                {performerError && <Alert type="error">{performerError}</Alert>}
                {performerMsg && <Alert type="success">{performerMsg}</Alert>}
                <div className="group">
                    <button type="submit" className="button">
                        Add or Invite Performer
                    </button>
                </div>
            </form>

            <div className="wizard-nav">
                <button type="button" className="button" onClick={onBack} disabled={isSaving}>
                    ← Back
                </button>
                <button type="button" className="button secondary" onClick={handleSaveAndExit} disabled={isSaving}>
                    Save and Edit Later
                </button>
                <button type="button" className="button primary" onClick={handleNext} disabled={isSaving}>
                    {isSaving ? 'Saving…' : 'Next: Publish →'}
                </button>
            </div>
        </div>
    );
}
